import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  Space,
  Typography,
  message,
} from "antd";
import {
  TwitterOutlined,
  FacebookOutlined,
  InstagramOutlined,
  YoutubeOutlined,
  LinkedinOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Text } = Typography;

interface SocialEmbedModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (embedData: { embed_type?: string; social_embed?: string }) => void;
  initialValue?: {
    embed_type?: string;
    social_embed?: string;
  };
}

export default function SocialEmbedModal({
  visible,
  onCancel,
  onSubmit,
  initialValue = {},
}: SocialEmbedModalProps) {
  const [form] = Form.useForm();

  const handleSubmit = (values: {
    embed_type: string;
    social_embed: string;
  }) => {
    if (!values.embed_type) {
      message.error("Please select an embed type");
      return;
    }
    if (!values.social_embed.trim()) {
      message.error("Please enter the embed code");
      return;
    }

    onSubmit({
      embed_type: values.embed_type,
      social_embed: values.social_embed,
    });
    form.resetFields();
  };

  const getPlatformIcon = (platform: string) => {
    const icons = {
      twitter: <TwitterOutlined style={{ color: "#1DA1F2" }} />,
      facebook: <FacebookOutlined style={{ color: "#4267B2" }} />,
      instagram: <InstagramOutlined style={{ color: "#E4405F" }} />,
      youtube: <YoutubeOutlined style={{ color: "#FF0000" }} />,
      linkedin: <LinkedinOutlined style={{ color: "#0077B5" }} />,
    };
    return icons[platform as keyof typeof icons] || null;
  };

  const getInstructions = (platform: string) => {
    const instructions = {
      twitter:
        '1. Go to the tweet → Click "..." → "Embed Tweet" → Copy the code',
      facebook: '1. Go to the post → Click "..." → "Embed" → Copy the code',
      instagram: '1. Go to the post → Click "..." → "Embed" → Copy the code',
      youtube:
        '1. Go to video → Click "Share" → "Embed" → Copy the iframe code',
      linkedin:
        '1. Use LinkedIn iframe embed format: <iframe src="https://www.linkedin.com/embed/feed/update/urn:li:share:YOUR_POST_ID" height="500" width="504" frameborder="0"></iframe>',
    };
    return instructions[platform as keyof typeof instructions] || "";
  };

  return (
    <Modal
      title="Add Social Media Embed"
      open={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      style={{ top: 20 }}
      width={600}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValue}
      >
        <Form.Item
          label="Embed Type"
          name="embed_type"
          rules={[{ required: true, message: "Please select an embed type!" }]}
        >
          <Select
            placeholder="Select social media platform"
            size="large"
            allowClear
          >
            <Select.Option value="twitter">
              <Space>
                {getPlatformIcon("twitter")}
                Twitter/X
              </Space>
            </Select.Option>
            <Select.Option value="facebook">
              <Space>
                {getPlatformIcon("facebook")}
                Facebook
              </Space>
            </Select.Option>
            <Select.Option value="instagram">
              <Space>
                {getPlatformIcon("instagram")}
                Instagram
              </Space>
            </Select.Option>
            <Select.Option value="youtube">
              <Space>
                {getPlatformIcon("youtube")}
                YouTube
              </Space>
            </Select.Option>
            <Select.Option value="linkedin">
              <Space>
                {getPlatformIcon("linkedin")}
                LinkedIn
              </Space>
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Embed Code"
          name="social_embed"
          rules={[{ required: true, message: "Please enter the embed code!" }]}
        >
          <TextArea
            rows={8}
            placeholder="Paste your social media embed code here..."
            style={{ fontFamily: "monospace" }}
          />
        </Form.Item>

        <Form.Item
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.embed_type !== currentValues.embed_type
          }
        >
          {({ getFieldValue }) => {
            const selectedPlatform = getFieldValue("embed_type");
            return selectedPlatform ? (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f6f8fa",
                  borderRadius: "6px",
                  marginBottom: "16px",
                }}
              >
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  <strong>Instructions for {selectedPlatform}:</strong>
                  <br />
                  {getInstructions(selectedPlatform)}
                </Text>
              </div>
            ) : null;
          }}
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button
              onClick={() => {
                form.resetFields();
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Add Embed
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
